import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@repo/ui/components/ui/avatar';
import { Button } from '@repo/ui/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/ui/table';
import { cn } from '@repo/ui/lib/utils';
import { Star } from 'lucide-react';
import { PhotographerActions } from './photographer-actions';
import {
  PHOTOGRAPHER_MODERATION_STATES,
  PHOTOGRAPHER_STATE_LABELS,
  type Photographer,
  type PhotographerState,
} from '@/services/photovogue';
import { PhotographerStateBadge } from '@/components/photovogue/state-badge/component';

export interface PhotographerTableProps {
  photographers: Photographer[];
  onRowClick: (photographer: Photographer) => void;
  onStateChange: (id: string, state: PhotographerState) => void;
  onSpotlightChange: (id: string, spotlight: boolean) => void;
  updatingIds: string[];
  onDelete: (id: string) => Promise<boolean>;
}

export const initials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

export const PhotographerTable = ({
  photographers,
  onRowClick,
  onStateChange,
  onSpotlightChange,
  updatingIds,
  onDelete,
}: PhotographerTableProps) => (
  <div className="rounded-lg border bg-white">
    <Table>
      <TableHeader>
        <TableRow variant="header">
          <TableHead>Photographer</TableHead>
          <TableHead>Location</TableHead>
          <TableHead>Spotlight</TableHead>
          <TableHead>State</TableHead>
          <TableHead className="text-right">Photos</TableHead>
          <TableHead className="text-right">Warnings</TableHead>
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {photographers.map((photographer) => (
          <TableRow
            key={photographer.id}
            onClick={() => onRowClick(photographer)}
            className={cn(
              photographer.totalPhotos === 0 &&
                'bg-zinc-50 text-cn-muted-foreground opacity-60',
            )}
          >
            <TableCell>
              <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9">
                  <AvatarImage
                    src={photographer.avatar}
                    alt={photographer.name}
                  />
                  <AvatarFallback>{initials(photographer.name)}</AvatarFallback>
                </Avatar>
                <span className="font-medium">{photographer.name}</span>
              </div>
            </TableCell>
            <TableCell className="text-cn-muted-foreground">
              {photographer.city}, {photographer.country}
            </TableCell>
            <TableCell>
              <Button
                type="button"
                size="sm"
                variant={photographer.tier === 'gold' ? 'secondary' : 'outline'}
                aria-pressed={photographer.tier === 'gold'}
                aria-label={`${photographer.tier === 'gold' ? 'Remove' : 'Add'} ${photographer.name} ${photographer.tier === 'gold' ? 'from' : 'to'} spotlight`}
                disabled={updatingIds.includes(photographer.id)}
                className={cn(
                  'gap-1.5',
                  photographer.tier === 'gold' &&
                    'border-yellow-300 bg-yellow-50 text-yellow-800 hover:bg-yellow-100',
                )}
                onClick={(event) => {
                  event.stopPropagation();
                  onSpotlightChange(
                    photographer.id,
                    photographer.tier !== 'gold',
                  );
                }}
              >
                <Star
                  className="h-3.5 w-3.5"
                  fill={photographer.tier === 'gold' ? 'currentColor' : 'none'}
                />
                {photographer.tier === 'gold' ? 'Spotlighted' : 'Spotlight'}
              </Button>
            </TableCell>
            <TableCell>
              <div
                className="flex items-center gap-2"
                onClick={(event) => event.stopPropagation()}
              >
                <PhotographerStateBadge state={photographer.state} />
                <select
                  aria-label={`Moderation state for ${photographer.name}`}
                  value={
                    photographer.state === 'to_fix' ? '' : photographer.state
                  }
                  disabled={updatingIds.includes(photographer.id)}
                  onChange={(event) =>
                    onStateChange(
                      photographer.id,
                      event.target.value as PhotographerState,
                    )
                  }
                  className="rounded-md border bg-white px-2 py-1 text-xs disabled:opacity-50"
                >
                  {photographer.state === 'to_fix' && (
                    <option value="" disabled hidden>
                      Change status
                    </option>
                  )}
                  {PHOTOGRAPHER_MODERATION_STATES.map((state) => (
                    <option key={state} value={state}>
                      {PHOTOGRAPHER_STATE_LABELS[state]}
                    </option>
                  ))}
                </select>
              </div>
            </TableCell>
            <TableCell className="text-right">
              {photographer.totalPhotos}
            </TableCell>
            <TableCell className="text-right">
              {photographer.warningCount}
            </TableCell>
            <TableCell onClick={(event) => event.stopPropagation()}>
              <PhotographerActions
                photographer={photographer}
                disabled={updatingIds.includes(photographer.id)}
                onDelete={onDelete}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export default PhotographerTable;
